// FooterSection.tsx — site footer for the master template.
// Supports four footer styles: columns (default), minimal, centered, large-mark.

import React from "react";
import { useLocation } from "react-router-dom";
import type { ThemeTokens, SectionCfg, TemplateNavigateFn, SectionOptions, CustomWebsitePage } from "../types";
import { useCreatorData, useCreatorSectionLoading } from "../templateData";
import { E, EL, useEdit } from "../InlineEdit";
import {
  NAV_OMIT_EDIT_KEY,
  NAV_ORDER_EDIT_KEY,
  defaultSlugForNavKey,
  navKeyToPageKey,
  parseNavOmit,
  parseNavOrder,
  resolveNavHref,
  resolveNavLabel,
  SITE_HEADER_NAV_DEFAULTS,
  websitePageTabUrl,
  type SiteHeaderNavKey,
} from "../websiteEditorInspectorTypes";
import { useTemplateAnalyticsOptional } from "../TemplateAnalyticsContext";
import { getCreatorSpaRelativePath } from "../../../../Utils/url/getCreatorSpaRelativePath";
import { isPublicWebsiteTemplateRoute } from "../../../../Utils/websiteTemplateGuards";
import { isNavOmitted } from "../websiteNavOmit";
import {
  BLOG_NAV_DISABLED_TIP,
  isBlogNavChromeLocked,
} from "../websiteEditorNav";
import { btnClass } from "./sectionUtils";
import * as socialCustomSync from "../../../../components/SocialLinks/socialCustomSync";

interface FooterProps {
  T: ThemeTokens;
  cfg?: SectionCfg;
  style?: string;
  onNavigate?: TemplateNavigateFn;
  customPages?: CustomWebsitePage[];
}

/** Skeleton shown while creator data is still loading — prevents mock data flash. */
function FooterSkeleton({ variant }: { variant: string }) {
  const isMinimal = variant === "minimal";
  const isLarge = variant === "large-mark";
  const isCentered = variant === "centered";

  if (isMinimal) {
    return (
      <footer className="mte-tpl-ft-minimal mte-tpl-ft-skel" aria-hidden>
        <div className="mte-tpl-ft-skel-bar mte-tpl-ft-skel-bar--copy" />
        <div className="mte-tpl-ft-skel-bar mte-tpl-ft-skel-bar--copy" />
      </footer>
    );
  }

  if (isCentered) {
    return (
      <footer className="mte-tpl-ft-centered mte-tpl-ft-centered--pad mte-tpl-ft-skel" aria-hidden>
        <div className="mte-tpl-ft-skel-bar mte-tpl-ft-skel-bar--display mte-tpl-ft-skel-bar--cx" />
        <div className="mte-tpl-ft-skel-inline mte-tpl-ft-skel-inline--cx">
          <div className="mte-tpl-ft-skel-bar mte-tpl-ft-skel-bar--link" />
          <div className="mte-tpl-ft-skel-bar mte-tpl-ft-skel-bar--link" />
          <div className="mte-tpl-ft-skel-bar mte-tpl-ft-skel-bar--link" />
          <div className="mte-tpl-ft-skel-bar mte-tpl-ft-skel-bar--link" />
        </div>
        <div className="mte-tpl-ft-skel-bar mte-tpl-ft-skel-bar--copy mte-tpl-ft-skel-bar--cx" />
      </footer>
    );
  }

  if (isLarge) {
    return (
      <footer className="mte-tpl-ft-large mte-tpl-ft-skel" aria-hidden>
        <div className="mte-tpl-ft-skel-bar mte-tpl-ft-skel-bar--display mte-tpl-ft-skel-bar--inv" />
        <div className="mte-tpl-ft-skel-cols">
          {[0, 1].map((i) => (
            <div key={i} className="mte-tpl-ft-skel-col">
              <div className="mte-tpl-ft-skel-bar mte-tpl-ft-skel-bar--hd mte-tpl-ft-skel-bar--inv" />
              <div className="mte-tpl-ft-skel-bar mte-tpl-ft-skel-bar--inv" />
              <div className="mte-tpl-ft-skel-bar mte-tpl-ft-skel-bar--inv" style={{ width: "70%" }} />
              <div className="mte-tpl-ft-skel-bar mte-tpl-ft-skel-bar--inv" style={{ width: "55%" }} />
            </div>
          ))}
        </div>
        <div className="mte-tpl-ft-skel-bar mte-tpl-ft-skel-bar--copy mte-tpl-ft-skel-bar--inv" />
      </footer>
    );
  }

  // columns (default)
  return (
    <footer className="mte-tpl-ft-columns mte-tpl-ft-skel" aria-hidden>
      <div className="mte-tpl-ft-skel-cols">
        <div className="mte-tpl-ft-skel-col">
          <div className="mte-tpl-ft-skel-bar mte-tpl-ft-skel-bar--name" />
        </div>
        {[0, 1].map((i) => (
          <div key={i} className="mte-tpl-ft-skel-col">
            <div className="mte-tpl-ft-skel-bar mte-tpl-ft-skel-bar--hd" />
            <div className="mte-tpl-ft-skel-bar" />
            <div className="mte-tpl-ft-skel-bar" style={{ width: "75%" }} />
            <div className="mte-tpl-ft-skel-bar" style={{ width: "60%" }} />
          </div>
        ))}
      </div>
      <div className="mte-tpl-ft-skel-bottom">
        <div className="mte-tpl-ft-skel-bar mte-tpl-ft-skel-bar--copy" />
      </div>
    </footer>
  );
}

export function FooterSection({
  T: _T,
  cfg,
  style: styleProp,
  onNavigate,
  customPages = [],
}: FooterProps) {
  const CREATOR = useCreatorData();
  const { get, editable } = useEdit();
  const location = useLocation();
  const isLoading = useCreatorSectionLoading(
    Boolean(CREATOR.bio?.name || CREATOR.bio?.social?.length || CREATOR.journal?.length),
  );
  const templateAnalytics = useTemplateAnalyticsOptional();
  const variant = styleProp || cfg?.style || "columns";
  const [socialLiveTick, setSocialLiveTick] = React.useState(0);
  React.useEffect(() => {
    return socialCustomSync.subscribeLiveSocialCustom(() =>
      setSocialLiveTick((n) => n + 1),
    );
  }, []);
  const displaySocial = React.useMemo(() => {
    const social = CREATOR.bio?.social ?? [];
    const resolve = socialCustomSync.resolveSocialLinksForDisplay;
    const resolved =
      typeof resolve === "function"
        ? resolve({
            social,
            userId: CREATOR.bio?.userId,
            username: CREATOR.bio?.username,
          })
        : social;
    // Newsletter is not a footer platform — keep it off the Platforms column.
    return resolved.filter(
      (s) =>
        !/newsletter/i.test(String(s.platform || "")) &&
        !/^social_newsletter$/i.test(String(s.platform || "")),
    );
    // socialLiveTick: refresh when custom links publish
  }, [
    CREATOR.bio?.social,
    CREATOR.bio?.userId,
    CREATOR.bio?.username,
    socialLiveTick,
  ]);

  // While live creator data is in-flight, show a skeleton so the footer
  // appears all at once with real content rather than flickering through mock data.
  if (isLoading) return <FooterSkeleton variant={variant} />;
  const go = (k: string, label: string) => (e: React.MouseEvent) => {
    if (onNavigate) {
      e.preventDefault();
      // blog → journal so footer matches header Nav Click entity_id buckets.
      const pageKey = navKeyToPageKey(k) || k;
      templateAnalytics?.trackNavClick(label, pageKey);
      onNavigate(pageKey);
    }
  };

  const trackPlatformClick = (platform: string, url: string) => {
    templateAnalytics?.trackSocialLink({
      platform,
      url,
      sectionId: "footer",
      sectionType: "Footer",
    });
  };
  // Match nav: undefined = still fetching contact_page; keep link until settled.
  const contactTabVisible =
    CREATOR.contact === undefined
      ? true
      : Boolean(CREATOR.contact?.enabled);
  const omit = new Set(parseNavOmit(get(NAV_OMIT_EDIT_KEY, "")));
  const allSiteLinks: [string, string][] = [
    ["Home", "home"],
    ["Products", "shop"],
    ["Blog", "blog"],
    ["Content", "content"],
    ["Link in Bio", "linkInBio"],
    ["About", "about"],
    ["Contact", "contact"],
    ...customPages
      .filter((p) => p.inNav)
      .map((p): [string, string] => [p.label, p.id]),
  ];
  const visibilityFiltered = allSiteLinks.filter(
    ([, k]) =>
      !isBlogNavChromeLocked(k) &&
      (k === "home" || !isNavOmitted(k, omit)) &&
      (k !== "contact" || contactTabVisible),
  );
  const order = parseNavOrder(get(NAV_ORDER_EDIT_KEY, "")).filter(
    (k) => !isBlogNavChromeLocked(k) && (k === "home" || !isNavOmitted(k, omit)),
  );
  const byKey = new Map(visibilityFiltered.map((row) => [row[1], row]));
  const SITE_NAV = [
    ...order
      .map((k) => byKey.get(k))
      .filter((row): row is [string, string] => Boolean(row)),
    ...visibilityFiltered.filter(([, k]) => !order.includes(k)),
  ];

  const usernameNorm = (CREATOR.bio.username ?? "").trim().toLowerCase();
  const footerHrefForKey = (k: string) => {
    const custom = customPages.find((p) => p.id === k);
    const fallback = custom
      ? `/${String(custom.slug || "").replace(/^\//, "")}`
      : defaultSlugForNavKey(k);
    const stored = resolveNavHref(get, k, fallback).trim();
    const path = (stored || fallback).replace(/^\//, "");
    const onWebsite = isPublicWebsiteTemplateRoute(location.pathname);
    if (onWebsite && usernameNorm) {
      return getCreatorSpaRelativePath({
        username: usernameNorm,
        route: path || undefined,
        pathname: location.pathname,
      });
    }
    return websitePageTabUrl(usernameNorm, stored || fallback);
  };

  const siteFooterRows = SITE_NAV.map(([label, navKey]) => {
    const live = resolveNavLabel(
      get,
      navKey,
      SITE_HEADER_NAV_DEFAULTS[navKey as SiteHeaderNavKey]?.label || label,
    );
    const pageKey = navKeyToPageKey(navKey);
    const path = `footer.navLabel.${pageKey === "journal" ? "journal" : navKey}`;
    const href = footerHrefForKey(navKey);
    return {
      liKey: `site-${navKey}`,
      navKey,
      path,
      label: live,
      href,
      onClick: (e: React.MouseEvent) => {
        const customHref = get(`${path}.href`, "").trim();
        if (customHref) return;
        go(pageKey, live)(e);
      },
    };
  });

  const renderSiteFooterLink = (row: (typeof siteFooterRows)[number]) => {
    if (isBlogNavChromeLocked(row.navKey)) {
      return (
        <span className="is-disabled" aria-disabled="true" title={BLOG_NAV_DISABLED_TIP}>
          {row.label}
        </span>
      );
    }
    return (
      <EL
        path={row.path}
        href={row.href}
        onClick={row.onClick}
        {...(editable ? {} : { navigateOnClick: true })}
      >
        {row.label}
      </EL>
    );
  };

  const platformFooterRows = displaySocial.map((s, index) => ({
    liKey: `plt-${index}-${s.platform}`,
    index,
    label: s.platform,
    href: s.url,
    onTrack: () => trackPlatformClick(s.platform, s.url),
  }));

  const footerColumns = {
    Site: siteFooterRows,
    Platforms: platformFooterRows,
  } as const;

  const footerName =
    CREATOR.about.profileName?.trim() ||
    CREATOR.bio.name?.trim() ||
    CREATOR.bio.handle;

  if (variant === "minimal")
    return (
      <footer className="mte-tpl-ft-minimal">
        <span>
          <E path="footer.minimalCopyright">{`© 2026 ${footerName}`}</E>
        </span>
        <span className="mte-tpl-ft-built">
          <E path="footer.builtOn">Built on Browsable</E>
        </span>
      </footer>
    );
  if (variant === "centered")
    return (
      <footer className="mte-tpl-ft-centered mte-tpl-ft-centered--pad">
        <div className="mte-tpl-ft-centered-name">
          <E path="footer.centeredBrandName">{footerName}</E>
        </div>
        <div className="mte-tpl-ft-centered-site">
          <div className="mte-tpl-ft-columns-col-h">
            <E path="footer.colHeading.Site">Site</E>
          </div>
          <div className="mte-tpl-ft-centered-nav">
            {siteFooterRows.map((row) => (
              <React.Fragment key={row.liKey}>
                {renderSiteFooterLink(row)}
              </React.Fragment>
            ))}
          </div>
        </div>
        <div className="mte-tpl-ft-centered-platforms">
          <div className="mte-tpl-ft-columns-col-h">
            <E path="footer.colHeadingPlatforms">Platforms</E>
          </div>
          <ul className="mte-tpl-ft-columns-ul mte-tpl-ft-centered-platforms-ul">
            {platformFooterRows.map((row) => (
              <li key={row.liKey}>
                <EL
                  path={`footer.platform.${row.index}`}
                  href={row.href}
                  lockText
                  navigateOnClick
                  onClick={row.onTrack}
                >
                  {row.label}
                </EL>
              </li>
            ))}
          </ul>
        </div>
        <div className="mte-tpl-ft-centered-copy">
          <E path="footer.centeredCopy">© 2026 · Built on Browsable</E>
        </div>
      </footer>
    );
  if (variant === "large-mark")
    return (
      <footer className="mte-tpl-ft-large">
        <div className="mte-tpl-ft-large-name">
          <E path="footer.largeMarkBrandName">{footerName}</E>
        </div>
        <div className="mte-tpl-ft-large-grid">
          {Object.entries(footerColumns).map(([h, rows]) => (
            <div key={h}>
              <div className="mte-tpl-ft-large-col-h">
                <E path={`footer.colHeading.${h}`}>{h}</E>
              </div>
              <ul className="mte-tpl-ft-large-ul">
                {rows.map((row) => (
                  <li key={row.liKey}>
                    {"onClick" in row && row.onClick ? (
                      renderSiteFooterLink(row)
                    ) : (
                      <EL
                        path={`footer.platform.${(row as { index: number }).index}`}
                        href={row.href}
                        lockText
                        navigateOnClick
                        onClick={"onTrack" in row ? (row as { onTrack: () => void }).onTrack : undefined}
                      >
                        {row.label}
                      </EL>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div className="mte-tpl-ft-large-bottom">
          <span>
            <E path="footer.largeMarkCopyright">{`© 2026 ${footerName}`}</E>
          </span>
          <span className="mte-tpl-ft-built">
            <E path="footer.builtOn">Built on Browsable</E>
          </span>
        </div>
      </footer>
    );
  if (variant === "stacked-cta")
    return (
      <footer className="mte-tpl-ft-stacked">
        <div className="mte-tpl-ft-stacked-inner">
          <E
            path="footer.stackedHeading"
            as="h2"
            className="mte-tpl-ft-stacked-h2"
          >
            Stay close.
          </E>
          <E
            path="footer.stackedLead"
            as="p"
            className="mte-tpl-ft-stacked-lead"
            multiline
          >
            One letter per Sunday. No spam.
          </E>
          <form className="mte-tpl-ft-stacked-form mte-tpl-ft-stacked-form--v">
            <p className="mte-tpl-ft-stacked-email-hint">
              <E path="footer.stackedEmailPlaceholder">your@email</E>
            </p>
            <div className="mte-tpl-ft-stacked-form-row">
              <input
                type="email"
                className="mte-tpl-ft-stacked-input"
                placeholder=""
                aria-label="Email"
              />
              <button
                type="button"
                className={`${btnClass("invertOnAccent")} mte-tpl-btn--bare`}
              >
                <E path="footer.stackedSubscribeCta">Subscribe →</E>
              </button>
            </div>
          </form>
        </div>
        <div className="mte-tpl-ft-stacked-bar">
          <span className="mte-tpl-ft-stacked-name">
            <E path="footer.stackedBarName">{footerName}</E>
          </span>
          <div className="mte-tpl-ft-stacked-bar-links">
            {siteFooterRows
              .filter((row) =>
                ["Home", "Shop", "Products", "Blog", "About", "Contact"].includes(row.label),
              )
              .map((row) => (
                <React.Fragment key={row.liKey}>
                  {renderSiteFooterLink(row)}
                </React.Fragment>
              ))}
          </div>
          <span>
            <E path="footer.stackedCopyright">© 2026</E>
          </span>
        </div>
      </footer>
    );
  // columns (default) — 3-column: name, Site links, Platforms
  return (
    <footer className="mte-tpl-ft-columns">
      <div className="mte-tpl-ft-columns-grid">
        <div>
          <div className="mte-tpl-ft-columns-name">
            <E path="footer.columnsBrandName">{footerName}</E>
          </div>
        </div>
        {Object.entries(footerColumns).map(([h, rows]) => (
          <div key={h}>
            <div className="mte-tpl-ft-columns-col-h">
              <E path={`footer.colHeading.${h}`}>{h}</E>
            </div>
            <ul className="mte-tpl-ft-columns-ul">
              {rows.map((row) => (
                <li key={row.liKey}>
                  {"onClick" in row && row.onClick ? (
                    renderSiteFooterLink(row)
                  ) : (
                    <EL
                      path={`footer.platform.${(row as { index: number }).index}`}
                      href={row.href}
                      lockText
                      navigateOnClick
                      onClick={"onTrack" in row ? (row as { onTrack: () => void }).onTrack : undefined}
                    >
                      {row.label}
                    </EL>
                  )}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      <div className="mte-tpl-ft-columns-bottom">
        <span>
          <E path="footer.columnsCopyrightLead">{`© 2026 ${footerName} · `}</E>
          <E path="footer.rightsReserved">All rights reserved</E>
        </span>
        <span className="mte-tpl-ft-built">
          <E path="footer.builtOn">Built on Browsable</E>
        </span>
      </div>
    </footer>
  );
}

FooterSection.options = {
  style: {
    kind: "radio",
    label: "Footer layout",
    options: ["columns", "minimal", "centered", "large-mark"],
    default: "columns",
  },
} as SectionOptions;
